// Pure rules constants. Nothing in core/ may import a renderer.

export const FACTIONS = ['order', 'balance', 'chaos'] as const;
export type Faction = (typeof FACTIONS)[number];

/**
 * Matching the original: Order blue, Balance green, Chaos red.
 *
 * Brighter and more saturated than a first pass would suggest, because they are
 * read through an exponential fog against a world graded deliberately dark. A
 * colour picked on a neutral background becomes a muddy grey at twenty metres,
 * and dark green and dark blue become indistinguishable. These are the only
 * saturated things on screen and they should look it.
 */
export const FACTION_COLOR: Record<Faction, number> = {
  order: 0x4a86e8,
  balance: 0x4cbf5c,
  chaos: 0xe8493c,
};

export const CLASS_IDS = ['magician', 'cleric', 'mentalist', 'arcanist'] as const;
export type ClassId = (typeof CLASS_IDS)[number];

export const ELEMENTS = ['fire', 'cold', 'earth', 'light', 'void', 'mana', 'physical'] as const;
export type Element = (typeof ELEMENTS)[number];

/** Only these four can be resisted. void/mana/physical always land full. */
/**
 * What a school is called on screen. Fire is "Heat" in the original's
 * protection lists and the name is worth keeping — "Resist Heat" reads as a
 * discipline, "Resist Fire" reads as a fire extinguisher.
 */
export const ELEMENT_LABEL: Record<Element, string> = {
  fire: 'Heat',
  cold: 'Cold',
  earth: 'Earth',
  light: 'Light',
  physical: 'Physical',
  void: 'Void',
  mana: 'Mana',
};

export const RESISTABLE: ReadonlySet<Element> = new Set<Element>(['fire', 'cold', 'earth', 'light']);

// ── Simulation ───────────────────────────────────────────────────────────────
export const TICK_HZ = 60;
export const TICK_DT = 1 / TICK_HZ;
export const SNAPSHOT_HZ = 20;

// ── Match ────────────────────────────────────────────────────────────────────
export const MATCH_SECONDS = 12 * 60;
/**
 * The freeze before a round starts, in seconds.
 *
 * Everybody spawned, nobody moving, the clock counting down to zero. It is the
 * moment a player works out which map they are on and where their side is, and
 * without it a round begins while the slowest machine in the room is still
 * drawing the first frame of it.
 */
export const WARMUP_SECONDS = 10;
export const TEAM_SIZE = 8;
/** Two of each class per team, enforced at team select. */
export const CLASS_SLOTS_PER_TEAM = 2;

// ── Movement ─────────────────────────────────────────────────────────────────
/*
 * Set against what the animation can carry, not only against how it feels.
 *
 * The locomotion clips are authored for 2.50 m/s at a jog and 3.29 at a sprint
 * (measured — see `tools/measure_stride.py`). The animator stretches playback to
 * close that gap but will not stretch it past `MAX_STRIDE_SCALE`, because a
 * sprint at twice its authored rate trades sliding feet for windmilling ones.
 *
 * At 7.0 the run needed 2.13× and could only have 1.8, so a sixth of the slide
 * survived however well the correction worked. At 5.8 it needs 1.76 and gets
 * all of it. These are about fifteen per cent off the old figures across the
 * board, which is a smaller change to how the game plays than it is to how it
 * looks.
 */
export const WALK_SPEED = 3.8;
export const RUN_SPEED = 5.8;
export const JUMP_VELOCITY = 5.0;
export const GRAVITY = 18.0;
/**
 * A ghost drifts; it does not fly. Was 9 m/s — faster than anybody alive could
 * run — which made a dead mage the best scout on the map. A fifth of that is
 * slower than a walk: enough to reach a Cleric who is coming for you, not
 * enough to tour the enemy's base while you wait.
 */
export const GHOST_SPEED = 1.8;

/**
 * Downward speed held against the floor while standing on it.
 *
 * Rapier's character controller works out whether it is grounded from the
 * movement it was asked to perform, so a mage standing perfectly still — zero
 * vertical movement — intermittently reports as airborne. That flicker reached
 * crouching, which needs to know it is on the ground, and the reticle, which
 * opens up in mid-air. A little constant downward pressure keeps the contact
 * unambiguous, and at 3cm a frame it is well inside the snap-to-ground distance.
 */
export const GROUND_STICK_SPEED = 2;
/**
 * How long a mage keeps counting as grounded after contact is lost. Absorbs the
 * odd bad frame on a step or a slope, and doubles as the coyote time every
 * first-person game wants anyway.
 */
export const COYOTE_SECONDS = 0.12;

/**
 * How far above or below an objective you can stand and still contest it.
 *
 * Presence is measured on the horizontal plane, which is right for a pool you
 * are standing beside and wrong for a gallery you are standing on: the Crypt
 * pool sits directly beneath the crypt deck, four and a half metres up. Three
 * metres is enough to cover a shrine's own dais and any step around a pool, and
 * not enough to reach a floor above.
 */
export const OBJECTIVE_HEIGHT_TOLERANCE = 3;

// ── Crouching ────────────────────────────────────────────────────────────────
/** Standing dimensions. The capsule, the eye and the hitbox all follow these. */
export const STAND_HEIGHT = 1.8;
/**
 * How wide a mage is.
 *
 * In core because both ends of the wire build a capsule from it, and a radius
 * that differed between them would be a player who fits through a doorway on
 * their own screen and not on the server — a rubber-band every few seconds with
 * nothing in any log to explain it. It lived as a private constant in the
 * renderer, which is precisely how that happens.
 */
export const BODY_RADIUS = 0.4;

/**
 * Whether a mage's own side blocks them.
 *
 * Mages block one another side-on (see `net/characterBodies.ts`). Blocking the
 * enemy in a doorway is tactics; blocking your own side's doorway is griefing,
 * and in a three-way fight your own team is the one most often in your way —
 * so teammates pass through each other, as they do in Team Fortress 2. Flip
 * this to make every mage solid to every other; both ends read it.
 */
export const BODY_BLOCK_TEAMMATES = false;

export const STAND_EYE = 1.55;
/**
 * Crouched. Two thirds of standing, which is enough to put your head below a
 * pool rim while leaving it above the water — the point being that you can sit
 * in a pool drawing ley and still shoot out of it.
 */
export const CROUCH_HEIGHT = 1.2;
export const CROUCH_EYE = 0.95;
/**
 * Slow enough that crouching is a commitment rather than a free improvement.
 *
 * The crouch clip is authored for 0.78 m/s, so even at 1.6 this is the one gait
 * still asking for more than the clamp will give — about a tenth of the slide
 * stays. Crouching is slow and rare enough that it is the cheapest place in the
 * game to leave that.
 */
export const CROUCH_SPEED = 1.6;
/** How fast the camera settles between the two, in metres per second. */
export const CROUCH_LERP_SPEED = 6;

// ── Accuracy ─────────────────────────────────────────────────────────────────
/**
 * Cone half-angles, radians. The reticle is drawn from the same numbers, so what
 * it shows is what the spell actually does — a reticle that widened without the
 * shot widening would be a lie the player works out within one fight.
 *
 * Only projectiles scatter. Arcs never miss by design, which is the whole reason
 * their damage sits under every other line; walls, signs and storms are placed
 * rather than thrown, and a wall landing three metres off is irritating rather
 * than interesting.
 */
export const SPREAD_CROUCHED = 0.0;
export const SPREAD_STILL = 0.006;
export const SPREAD_WALKING = 0.028;
export const SPREAD_RUNNING = 0.060;
/** Worst of all: nothing to brace against. */
export const SPREAD_AIRBORNE = 0.075;

// ── Headshots ────────────────────────────────────────────────────────────────
/**
 * Damage multiplier for a hit in the top band of the body.
 *
 * The hitbox is a capsule, so the "head" is a zone rather than a modelled part.
 * That is honest for now — but it does mean the band has to be generous enough
 * to hit and small enough to earn, and that it will want revisiting once mages
 * are actual meshes with actual proportions.
 */
export const HEADSHOT_MULTIPLIER = 1.5;
/** Fraction of standing height, measured from the top down. */
export const HEADSHOT_BAND = 0.19;
/**
 * How fast a flying mage crosses the ground: exactly as fast as running.
 *
 * Flight was quicker than sprinting in every direction, which made it a better
 * way to travel rather than a different one — a Mentalist simply flew
 * everywhere. Matching the run means height is the thing flight buys, and the
 * price is paid in fatigue.
 */
export const FLY_SPEED = RUN_SPEED;
/**
 * How fast it climbs. Half of what the whole thing used to move at.
 *
 * Rising has to be a commitment. Going up quickly is what let a Mentalist pop
 * over a wall mid-fight and be gone before anything could be aimed at it, and
 * the slower climb is what turns that into a decision somebody can punish.
 */
export const FLY_CLIMB_SPEED = 4.0;

// ── Fatigue ──────────────────────────────────────────────────────────────────
export const FATIGUE_MAX = 100;
export const FATIGUE_REGEN_PER_SEC = 34;
export const FATIGUE_RUN_DRAIN_PER_SEC = 14;
export const FATIGUE_FLY_DRAIN_PER_SEC = 22;
export const FATIGUE_CAST_FLOOR = 5;

/**
 * Fatigue you have to recover before you can run again once you have run out.
 *
 * Without it, a mage at zero fatigue flickers between running and walking every
 * few ticks — one tick of not-running regenerates enough to run for two more,
 * which empties it again. Speed, and the reticle drawn from it, strobed. Being
 * winded is now a state you have to come out of rather than a boundary you
 * vibrate against.
 */
export const FATIGUE_RUN_RESUME = 15;

/**
 * Flight also burns power, as a fraction of maximum per second so that it costs
 * a level 30 Mentalist the same share as a level 1 one.
 *
 * Fatigue alone never held flight down: it refills at 34/s, so hopping — up for
 * four seconds, down for three, repeat — was free and unlimited. Power refills at
 * ley rate, and a Mentalist's ley is a flat 0.5 everywhere, which pays 7% of
 * maximum per second. Drawing 12% against that means altitude costs ammunition.
 */
export const FLY_PP_DRAIN_FRACTION = 0.12;
/**
 * Power a Mentalist has to recover before they can take off again, as a
 * fraction of maximum.
 *
 * Without it, flight stutters exactly the way running did at the bottom of the
 * fatigue bar: power hits zero, flight cuts out, a single point regenerates, and
 * the held key puts them straight back in the air to spend it. Being drained is
 * a state to come out of, not a line to bounce off.
 */
export const FLY_PP_RESUME_FRACTION = 0.15;

// ── Casting ──────────────────────────────────────────────────────────────────
/**
 * Movement multiplier while a spell is winding up.
 *
 * The whole cost of a cast time is that you cannot dodge as well while you pay
 * it. Nothing interrupts a cast — not damage, not a root, not being launched —
 * so the risk is entirely that a Fire Ball's half second is half a second of
 * being easier to hit.
 */
export const CASTING_MOVE_SCALE = 0.5;

/**
 * How close a Cleric has to be to raise a fallen teammate.
 *
 * There was no limit at all: `tryRaiseDead` took the nearest ghost anywhere in
 * the arena, so a Cleric could pull somebody up from eighty metres away without
 * moving. That made Call Dead free rather than a thing you go and do, and it
 * would have let a Cleric bot hold its shrine and resurrect the whole team from
 * there.
 */
export const RAISE_RANGE = 35;
/**
 * Cost of calling somebody back, as a fraction of maximum.
 *
 * A raised mage arrives on their feet but with almost nothing behind them —
 * a fifth of their health, power and breath. Without that, a Cleric standing
 * behind a fight simply undoes it, and dying stops meaning anything at all.
 */
/**
 * How much of a mage comes back, by the tier of the Resurrect that raised them.
 *
 * A fifth at first, four fifths at the top. The point is that a raise early in a
 * match hands somebody back barely standing — worth having, and not worth
 * standing still for — while a Cleric who has earned it can put a teammate back
 * on their feet ready to fight.
 */
export const RAISE_FRACTIONS = [0.2, 0.35, 0.5, 0.65, 0.8] as const;

/** Reach of Call Ghost: it pulls, so it pulls from across a room. */
export const CALL_RANGE = 35;
/**
 * Reach of Resurrect. Short, because you have just pulled them to your feet —
 * and short enough that a Cleric cannot raise across a fight.
 */
export const RESURRECT_RANGE = 6;

/** Where a called ghost lands, in front of the Cleric. */
export const CALL_DISTANCE = 2;
/** How long before a Cleric can call again. */
export const RAISE_COOLDOWN = 8;

// ── Hit points ───────────────────────────────────────────────────────────────
/** The manual: HP trickles back while walking, faster when standing still. */
export const HP_REGEN_MOVING = 0.4;
export const HP_REGEN_STILL = 1.6;

// ── Ley (power regen rate, 0..1) ─────────────────────────────────────────────
/** At ley 1.0 you regain this fraction of max PP per second. */
export const PP_REGEN_AT_FULL_LEY = 0.14;
export const LEY_SHRINE_RANGE = 150;
export const LEY_POOL_RANGE = 40;
/** Mentalist ley is flat everywhere: good, never outstanding. */
export const MENTALIST_FLAT_LEY = 0.5;
/** Arcanist holds full charge this long after leaving a pool, then drops fast. */
export const ARCANIST_HOLD_SECONDS = 20;
export const ARCANIST_DECAY_SECONDS = 4;

// ── Area control ─────────────────────────────────────────────────────────────
export const POOL_CAPTURE_SECONDS = 8;
export const POOL_INNER_RADIUS = 4.0;
export const POOL_DECAY_PER_SEC = 0.03;

export const SHRINE_POWER_MAX = 100;
export const SHRINE_DRAIN_PER_SEC = 8;
export const SHRINE_RESTORE_PER_SEC = 6;
export const SHRINE_INNER_RADIUS = 5.0;

// ── Placed effects ───────────────────────────────────────────────────────────
/** How far ahead a ground-targeted spell will look for a floor. */
export const STORM_CAST_RANGE = 45;
/** Storms roll their dice once per interval, not once per cast. */
export const STORM_TICK_SECONDS = 1;
/**
 * Vertical reach of a storm. Deliberately short of the 9m interior: a Mentalist
 * who spends power to get above one has earned the dodge.
 */
export const STORM_HEIGHT = 4;

// ── Barriers ─────────────────────────────────────────────────────────────────
/** How far in front of the caster a wall springs up. */
export const WALL_DISTANCE = 3.2;
export const WALL_WIDTH = 6;
export const WALL_HEIGHT = 3.5;
/**
 * Thin on purpose. A wall you can be *inside* for a whole second is a room, and
 * the pass-through effects fire on entry precisely because nobody lingers in one.
 */
export const WALL_THICKNESS = 0.6;
/** Clearance kept between a wall and whatever masonry the caster is facing. */
export const WALL_CLEARANCE = 0.5;

/**
 * Signs arm at this radius, and the floor decal is drawn to match it exactly.
 * Small is the whole point — the original's signs were missable because they
 * were physically small, and a trap that arms four metres away reads as broken
 * rather than sneaky.
 */
export const SIGN_TRIGGER_RADIUS = 0.9;
/**
 * Live signs per Arcanist. A fourth cast fizzles the oldest. Without a cap, a
 * 45-second life on an 8-second cooldown makes a doorway impassable for free.
 */
export const SIGN_MAX_PER_CASTER = 3;

/**
 * Signs do not arm instantly. Expulsion triggers on its own caster by design, so
 * without a moment's delay laying one would be indistinguishable from casting a
 * jump — you could never leave one behind as a trap.
 */
export const SIGN_ARM_SECONDS = 0.35;
/** How long Sign of Hesitation holds someone still. */
export const SIGN_ROOT_SECONDS = 1.5;

/**
 * Expulsion launch, metres per second.
 *
 * The Crypt gallery's deck sits at y = 4.8, and under 18 m/s² of gravity
 * reaching it needs 13.15. Fourteen clears it with enough margin that landing on
 * the lip rather than the deck is not a coin toss — that ledge is the whole
 * reason the spell is the Arcanist's answer to flight.
 */
export const EXPULSE_IMPULSE_UP = 14;
/**
 * Sideways component, away from the sign's centre. Standing dead on one lifts you
 * straight up, which is what makes it usable on purpose; being caught near the
 * edge throws you, which is what makes it usable as a trap.
 */
export const EXPULSE_IMPULSE_OUT = 2.5;

/** Horizontal impulse velocity decays by this factor per second. */
export const IMPULSE_DECAY_PER_SEC = 0.12;

/**
 * How much more damage a fractured target takes, as a fraction. Applied as a
 * negative entry in the resistance sum, which is where the whole mechanic lives.
 */
export const FRACTURE_VULNERABILITY = 0.25;

// ── Burning ──────────────────────────────────────────────────────────────────
/**
 * Fire's signature: a stack of one-damage ticks that a second hit extends
 * rather than stacking. Small numbers on purpose — the point is that Fire wins
 * a long exchange it would lose on the raw dice, not that it out-damages Earth.
 */
export const BURN_TICK_SECONDS = 0.5;
/**
 * How long Pain goes on hurting, and how often.
 *
 * Refreshed rather than extended: a second Pain sets the clock back to four
 * seconds, it does not make it eight. Burning stacks because a Magician's whole
 * game is landing dart after dart; the Mentalist's is not, and a stacking Pain
 * would quietly make it one.
 */
export const SHOCK_SECONDS = 4;
export const SHOCK_TICK_SECONDS = 0.5;
export const BURN_TICK_DAMAGE = 1;
/** Ten ticks. However hard you are burning, five seconds is the whole of it. */
export const BURN_MAX_SECONDS = 5;

// ── Valhalla ─────────────────────────────────────────────────────────────────
/**
 * Distance between the drop-in point and the teleporter.
 *
 * Sized against the fatigue bar rather than picked round. A full bar is 100 and
 * running costs 14/s, so you get 7.14 seconds of sprint, which at 5.8 m/s is
 * 41.4m. The teleporter arms 2.5m out, so the walk that matters is 45.5m — four
 * metres more than one bar will carry you. You finish every crossing at a walk
 * no matter how you pace it, which is the penalty. Total: a shade over eight
 * seconds.
 *
 * This is measured in metres and the penalty is measured in seconds, so it does
 * not survive a change to `RUN_SPEED` on its own — dropping the run from 7 to
 * 5.8 stretched the crossing to ten and a half seconds until this came down to
 * match. The assertion in `valhalla.test.ts` is what caught it.
 */
export const VALHALLA_WALK = 48;
/** Interior length of the corridor, with a little room behind each end. */
export const VALHALLA_LENGTH = VALHALLA_WALK + 8;

// ── Death ────────────────────────────────────────────────────────────────────
export const RESURRECT_PP_COST = 5;
export const RESURRECT_FATIGUE_COST = 45;
